import type { FormaPago } from '@abastece/core';
import type { CuentaBanco, CuentaBancoCordobas } from '@abastece/shared';

/**
 * A purchase paid in parts, from «Mercadería comprada» (the owner, 2026-10-04): a row for each part
 * paid some way, and the purchase's own «Forma de pago» for what they leave, credit included. The
 * trucker paid in cash for goods the supplier gives on credit is the case that asked for it. An
 * expense in «Gastos» is paid in parts the same way since 2026-10-16 (`pagoEnPartes`).
 */

/** How a part can be paid: anything but credit, which is what is left owing. */
export type FormaParte = 'efectivo_cordobas' | 'efectivo_dolares' | 'transferencia' | 'billetera';

export const FORMAS_PARTE: readonly FormaParte[] = ['efectivo_cordobas', 'efectivo_dolares', 'transferencia', 'billetera'];

/** A part as the event takes it: its amount in the purchase's currency, read. */
export interface Parte {
  pagoId: string;
  formaPago: FormaParte;
  monto: number;
  /** A part by transfer: the córdoba account it came from. */
  cuenta?: CuentaBancoCordobas | undefined;
}

/** What the parts leave for «Forma de pago», in the currency of what is paid: below nothing, they pay too much. */
export function restoDeLasPartes(total: number, partes: readonly Pick<Parte, 'monto'>[]): number {
  return partes.reduce((a, x) => a - x.monto, total);
}

/** A purchase's (`restoDeLasPartes`). */
export const restoDeLaCompra = restoDeLasPartes;

/**
 * How the purchase was paid, as `compra.registrada` carries it: as `pagoEnPartes` has it, with the
 * day it falls due when what the parts leave is owed.
 */
export function pagoDeLaCompra(x: Parameters<typeof pagoEnPartes>[0] & { vencePago: string }): ReturnType<typeof pagoEnPartes> & {
  vencePago?: string;
} {
  const { formaPago, ...resto } = pagoEnPartes(x);
  return { formaPago, ...(formaPago === 'credito' ? { vencePago: x.vencePago } : {}), ...resto };
}

/**
 * How something paid in parts was paid, as `compra.registrada` and, since 2026-10-16,
 * `gasto.registrado` carry it. The parts go as `pagos`, and `formaPago` pays what they leave: always
 * something, so parts that pay it all hand their last one over to it, its account with it. Córdobas
 * in cash come out of the drawer when it is on (`sesionId`), every one of them: the screen asks once
 * for all of it. What goes by transfer names its bank account (`cuenta`). Without parts it is what
 * the form sent before.
 */
export function pagoEnPartes(x: {
  total: number;
  partes: readonly Parte[];
  formaPago: FormaPago;
  /** The open till, when córdobas in cash come out of it. */
  sesionId?: string | undefined;
  /** The bank account `formaPago` pays from, when it is a transfer. */
  cuenta?: CuentaBanco | undefined;
}): {
  formaPago: FormaPago;
  sesionId?: string;
  cuenta?: CuentaBanco;
  pagos?: { pagoId: string; formaPago: FormaParte; monto: number; sesionId?: string; cuenta?: CuentaBancoCordobas }[];
} {
  const partes = [...x.partes];
  let formaPago = x.formaPago;
  let cuenta = x.cuenta;
  if (partes.length > 0 && restoDeLaCompra(x.total, partes) === 0) {
    const ultima = partes.pop()!;
    formaPago = ultima.formaPago;
    cuenta = ultima.cuenta;
  }
  const deLaGaveta = (forma: FormaPago) => (x.sesionId && forma === 'efectivo_cordobas' ? { sesionId: x.sesionId } : {});
  const delBanco = <C extends CuentaBanco>(forma: FormaPago, c: C | undefined) => (forma === 'transferencia' && c ? { cuenta: c } : {});
  return {
    formaPago,
    ...deLaGaveta(formaPago),
    ...delBanco(formaPago, cuenta),
    ...(partes.length > 0
      ? {
          pagos: partes.map((p) => ({
            pagoId: p.pagoId,
            formaPago: p.formaPago,
            monto: p.monto,
            ...deLaGaveta(p.formaPago),
            ...delBanco(p.formaPago, p.cuenta),
          })),
        }
      : {}),
  };
}
